import { CircularProgress } from '@mui/material'
import { Highlight } from 'components/Images/Highlights/HighlightTypes'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import React, { CSSProperties, Ref, useDeferredValue } from 'react'
import {
  PdfFeature,
  PdfPage,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import EngDrawingsHighlights from './EngDrawingsHighlight'
import { useZoomAndPanEffects } from './hooks/useZoomAndPanEffects'
import { useZoomAndPanRefs } from './hooks/useZoomAndPanRefs'
import { useZoomAndPanStates } from './hooks/useZoomAndPanStates'

export type Vect2D = {
  x: number
  y: number
}

export type ZoomAndPanImgProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  bomItemPointer: BomItemPointer
  onScaleChanged?: (scale: number) => void
  options?: Partial<typeof zoomAndPanDefaultOptions>
}

const divStyles: CSSProperties = {
  position: 'relative',
  overflow: 'hidden',
  width: '100%',
  height: '100%',
} as CSSProperties

const imgStyles: CSSProperties = {
  transformOrigin: 'center center',
  userSelect: 'none',
  cursor: 'grab',
  willChange: 'transform',
}

const imgWrapperStyles: CSSProperties = {
  transformOrigin: '0 0',
}

export type ZoomAndPanImgRef = {
  zoomIn: () => void
  zoomOut: () => void
  fitPage: () => void
  highlightArea: (
    highlights: Array<{
      pdfPage: PdfPage
      pdfFeature: PdfFeature
      highlight: Highlight
    }>,
  ) => void
  clearHighlights: () => void
  rotateImage: (angle: string) => void
  zoomToScale: (newScale: number) => void
  getScale: () => number
  position: (position: Vect2D) => void
  setZoomAndPosition: (zoom: number, position: Vect2D) => void
  getImgWidth: () => number
  getImgHeight: () => number
}

const zoomAndPanDefaultOptions = {
  disableWheelZoom: false,
  zoomSpeed: 0.001,
  minScale: 0.001,
  maxScale: 10,
  paddingTopFix: 32,
}

export const ZoomAndPanImg = React.forwardRef(
  (props: ZoomAndPanImgProps, ref: Ref<ZoomAndPanImgRef>) => {
    const {
      options: optionsFromProps,
      onScaleChanged,
      bomItemPointer,
      ...rest
    } = props

    const refs = useZoomAndPanRefs(optionsFromProps)
    const states = useZoomAndPanStates()
    const effects = useZoomAndPanEffects(props, refs, states, onScaleChanged)
    const { divRef, imgRef, divWrapperRef } = refs
    const {
      highlights,
      isLoading,
      scale,
      position,
      rotation,
      setHighlights,
      setRotation,
      setPosition,
    } = states

    const {
      handleSetPosition,
      zoomAndPan,
      onWheel,
      onMouseDown,
      onMouseMove,
      onMouseUp,
      onDoubleClick,
      fitScreen,
      getContainerCenter,
    } = effects

    React.useImperativeHandle(ref, () => ({
      zoomToScale: (newScale: number) => {
        if (!divRef.current) return

        const { cx, cy } = getContainerCenter()
        zoomAndPan(cx, cy, 0, newScale)
      },
      getScale: () => {
        return scale
      },
      zoomIn: () => {
        if (!imgRef.current) return

        const imgRect = imgRef.current?.getBoundingClientRect()

        const centerX = imgRect.left + imgRect.width / 2
        const centerY = imgRect.top + 64 + imgRect.height / 2

        zoomAndPan(centerX, centerY, -100)
      },
      zoomOut: () => {
        if (!divRef.current) return

        const containerRect = divRef.current.getBoundingClientRect()
        const centerX = containerRect.left + containerRect.width / 2
        const centerY = containerRect.top + containerRect.height / 2

        zoomAndPan(centerX, centerY, 100)
      },
      setZoomAndPosition: (zoom: number, position: Vect2D) => {
        zoomAndPan(null, null, null, zoom, position)
      },
      fitPage: fitScreen,
      highlightArea: (
        highlights: Array<{
          pdfPage: PdfPage
          pdfFeature: PdfFeature
          highlight: Highlight
        }>,
      ) => {
        const highlightsToSet = {}

        highlights.forEach(({ pdfPage, pdfFeature, highlight }) => {
          if (!highlight?.id) {
            console.error('highlights should have an id', highlight)
            return
          }

          highlightsToSet[highlight.id] = {
            highlight,
            pdfPage,
            pdfFeature,
          }
        })

        setHighlights(highlightsToSet)
      },
      clearHighlights: () => {
        // setHighlights({})
      },
      rotateImage: (angle: string) => {
        if (imgRef.current) {
          let nextRotation = parseInt(rotation) + parseInt(angle)
          nextRotation = nextRotation % 360

          setRotation(`${nextRotation}deg`)
        }
      },
      position: (position: Vect2D) => {
        const containerRect = divRef.current.getBoundingClientRect()
        const containerCenterX = (containerRect.width - 70) / 2
        const containerCenterY =
          (containerRect.height - refs._options.current.paddingTopFix) / 2

        const newPosition = {
          x: containerCenterX - position.x * scale,
          y: containerCenterY - position.y * scale,
        }

        setPosition(newPosition)

        handleSetPosition(newPosition, scale)
      },
      getImgWidth: () => {
        if (imgRef.current) {
          return imgRef.current.naturalWidth
        }

        return 0
      },
      getImgHeight: () => {
        if (imgRef.current) {
          return imgRef.current.naturalHeight
        }

        return 0
      },
    }))

    const imgRefWidth = useDeferredValue(() => {
      if (imgRef.current) {
        return imgRef.current.naturalWidth
      }
      return 1
    })

    const imgRefHeight = useDeferredValue(() => {
      if (imgRef.current) {
        return imgRef.current.naturalHeight
      }
      return 1
    })

    if (!props.src) {
      return null
    }

    return (
      <>
        {isLoading === 1 && (
          <EngDrawingsHighlights
            highlights={highlights}
            divRef={divRef}
            scale={scale}
            position={position}
            imgHeight={imgRefHeight()}
            imgWidth={imgRefWidth()}
            rotation={rotation}
            bomItemPointer={props.bomItemPointer}
            paddingTopFix={refs._options.current.paddingTopFix}

          />
        )}
        <div
          ref={divRef}
          style={divStyles}
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          onMouseMove={onMouseMove}
          onWheel={onWheel}
          onDoubleClick={onDoubleClick}
        >
          {isLoading < 1 && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress />
            </div>
          )}
          <div ref={divWrapperRef} style={imgWrapperStyles}>
            <img
              {...rest}
              ref={imgRef}
              style={imgStyles}
              draggable={false}
              onContextMenu={(e) => {
                e.preventDefault()
              }}
            />
          </div>
        </div>
      </>
    )
  },
)

ZoomAndPanImg.displayName = 'ZoomAndPanImg'
